import {instance_manager, InstanceAPIConnectorError} from "../lib/InstanceManager.ts";
import {swal_util} from "../lib/SwalUtil.ts";
import {frontBaseUrl} from "../build_constants.ts";
import {portForwardingDialogManager} from "../pages/instance/PortForwardingDialogManager.tsx";
import type {InstanceDataProps} from "../lib/ModelTypes.ts";
import type {NavigateFunction} from "react-router-dom";
import type {TerminalTab} from "../pages/terminal/container/TerminalContainerContext.ts";
import { t } from "i18next";


export interface ActionArgs {
    instanceId: number,
    tab?: TerminalTab,
    closeTab?: (key: number) => void,
    removeTaByReqKey?: (key: string) => void,
    showCreateSnapshotModal?: (open: boolean) => void,
    setShowPortForwardingModal?: (open: boolean) => void,
    setData?: (data: (prev: InstanceDataProps) => InstanceDataProps) => void,
    setPageLoading?: (data: boolean) => void,
    setLoadingText?: (data: string) => void
    navigateFunc?: NavigateFunction
}

interface ActionHandler {
    (args: ActionArgs): Promise<boolean>;
}

function isFirefox() {
    return typeof InstallTrigger !== 'undefined' || navigator.userAgent.toLowerCase().includes('firefox');
}

function normalizeInstanceType(type?: string): "CPU" | "GPU" | undefined {
    if (!type) return undefined;
    const normalized = type.toUpperCase();
    if (normalized === "CPU" || normalized === "GPU") {
        return normalized;
    }
    return undefined;
}


// noinspection JSUnusedGlobalSymbols
const actionHandlerMap = {
    async doClose(args: ActionArgs) {
        const {tab, closeTab, removeTaByReqKey} = args;
        if (tab) {
            const tabKey = tab.tabKey
            if (!closeTab || !removeTaByReqKey)
                console.error("closeTab or removeTaByReqKey is null");
            else if (tabKey) {
                closeTab(tabKey);
            } else {
                removeTaByReqKey(tab.reqKey);
            }
        } else
            console.error("tab is null");
        return false;
    },

    async doCreateSnapshot(args: ActionArgs) {
        const {showCreateSnapshotModal} = args;
        if (!showCreateSnapshotModal) {
            console.error("showCreateSnapshotModal is null");
            return false;
        }
        showCreateSnapshotModal(true);
        return true;
    },

    async doHardRestart(args: ActionArgs) {
        const {instanceId, setPageLoading} = args;

        try {
            setPageLoading?.(true);
            await instance_manager.instanceAction.hardRestart(instanceId);
            await swal_util.show_info(t("instance.restartInfo"));
            return true;
        } catch (err) {
            if (err instanceof InstanceAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(t("common.requestFailed"));
            }

            console.error(err);
            return false;
        } finally {
            setPageLoading?.(false);
        }
    },

    async doShutdown(args: ActionArgs) {
        const {instanceId, setPageLoading} = args;

        try {
            setPageLoading?.(true);
            await instance_manager.instanceAction.shutdown(instanceId);
            await swal_util.show_success(t("common.requestSuccess"));
            // location.reload();

            return true;
        } catch (err) {
            if (err instanceof InstanceAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(t("common.requestFailed"));
            }
            console.error(err);
        } finally {
            setPageLoading?.(false);
        }

        return false;
    },

    async doBoot(args: ActionArgs) {
        const {instanceId, setPageLoading} = args;

        try {
            setPageLoading?.(true);
            await instance_manager.instanceAction.boot(instanceId);
            await swal_util.show_success(t("common.requestSuccess"));
            return true;
        } catch (err) {
            if (err instanceof InstanceAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(t("common.requestFailed"));
            }
            console.error(err);
        } finally {
            setPageLoading?.(false);
        }

        return false;
    },

    async doDelete(args: ActionArgs) {
        const {instanceId, setPageLoading} = args;

        const data = await swal_util.show_question(
            t("instance.deleteTitle"),            // "인스턴스 삭제"
            t("instance.deleteConfirm")           // "인스턴스를 삭제하시겠습니까?"
        );
        if (data.isConfirmed) {
            try {
                setPageLoading?.(true);
                await instance_manager.deleteInstance(instanceId);
                await swal_util.show_success(t("common.requestSuccess"));
                // location.reload();

                return true;
            } catch (err) {
                if (err instanceof InstanceAPIConnectorError) {
                    await swal_util.show_error(`${err.localization}`);
                } else {
                    await swal_util.show_error(t("common.requestFailed"));
                }

                console.error(err);
            } finally {
                setPageLoading?.(false);
            }
        }

        return false;
    },

    async doPause(args: ActionArgs) {
        const {instanceId, setPageLoading} = args;

        try {
            setPageLoading?.(true);

            await instance_manager.instanceAction.pause(instanceId);
            await swal_util.show_success(t("common.requestSuccess"));
            // location.reload();

            return true;
        } catch (err) {
            if (err instanceof InstanceAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(t("common.requestFailed"));
            }

            console.error(err);
        } finally {
            setPageLoading?.(false);
        }

        return false;
    },

    async doResume(args: ActionArgs) {
        const {instanceId, setPageLoading} = args;

        try {
            setPageLoading?.(true);
            await instance_manager.instanceAction.resume(instanceId);
            await swal_util.show_success(t("common.requestSuccess"));
            // location.reload();
            return true;
        } catch (err) {
            if (err instanceof InstanceAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(t("common.requestFailed"));
            }
            if (!setPageLoading)
                console.warn("setPageLoading is null");
            else

            console.error(err);
        } finally {
            setPageLoading?.(false);
        }

        return false;
    },

    async doOpenWebSshTerminal(args: ActionArgs) {
        const {instanceId} = args;
        if (isFirefox()) {
            await swal_util.show_warning(t("instance.warning"));
        }
        let instanceType = normalizeInstanceType(
            instance_manager.instances.find((instance) => instance.id === instanceId)?.flavor?.type
        );

        if (!instanceType) {
            try {
                const detail = await instance_manager.getInstanceDetail(`${instanceId}`);
                instanceType = normalizeInstanceType(detail?.data?.data?.flavor?.type);
            } catch (err) {
                console.error(err);
            }
        }

        const instancePath = instanceType === "GPU" ? "bms-instance" : "vm-instance";
        window.open(`${frontBaseUrl}/${instancePath}/terminal/${instanceId}`);
        return true;
    },

    async doOpenWebSshTerminalOld(args: ActionArgs) {
        const {instanceId} = args;
        window.open(`${frontBaseUrl}/instance/terminal_old?instance=${instanceId}`);
        return true;
    },

    async doShowPortForwarding(args: ActionArgs) {
        const {instanceId, setShowPortForwardingModal, setData} = args;

        if (!setShowPortForwardingModal) {
            console.error("setShowPortForwardingModal is null");
            return false;
        }

        if (!setData) {
            console.error("setData is null");
            return false;
        }

        await portForwardingDialogManager.show(instanceId, setShowPortForwardingModal, setData);
        return true;
    },

    async doOpenVncConsole(args: ActionArgs) {
        const {instanceId} = args;
        if (isFirefox()) {
            await swal_util.show_warning(t("instance.warning"));
        }
        let instanceType = normalizeInstanceType(
            instance_manager.instances.find((instance) => instance.id === instanceId)?.flavor?.type
        );

        if (!instanceType) {
            try {
                const detail = await instance_manager.getInstanceDetail(`${instanceId}`);
                instanceType = normalizeInstanceType(detail?.data?.data?.flavor?.type);
            } catch (err) {
                console.error(err);
            }
        }

        const instancePath = instanceType === "GPU" ? "bms-instance" : "vm-instance";
        window.open(`${frontBaseUrl}/${instancePath}/vnc/${instanceId}`);
        return true;
    }
}


/**
 * kebabCase 문자열을 camelCase 문자열로 변환하고 선택적으로 첫 글자를 대문자로 접두사 앞에 추가
 *
 * @param {string} input - 변환할 케밥 케이스 문자열
 * @param {string} [prefix='do'] - 결과 앞에 추가할 선택적 접두사
 * @return {string} 선택적 접두사가 있는 변환된 camelCase 문자열
 */
function kebabToDoCamelCase(input: string, prefix: string = "do"): string {
    const parts = input.split('-');

    const camelCase = parts.map((part, index) =>
        index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    ).join('');

    if (prefix === "") {
        return camelCase;
    }

    return prefix + camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
}


/**
 * 제공된 작업 이름을 기반으로 작업 핸들러를 확인합니다.
 *
 * @param {string} actionName - 핸들러명
 * @return {ActionHandler|undefined} 핸들러명에 `boot` 가 주어지면 `actionHandlerMap`의 `doBoot`가 반환
 */
export function resolveActionHandler(actionName: string): ActionHandler {
    const actionKey = kebabToDoCamelCase(actionName) as keyof typeof actionHandlerMap;

    const ret = actionHandlerMap[actionKey] as unknown as (args: ActionArgs) => Promise<boolean>
    if (!ret) {
        throw new Error(`actionHandlerMap[${actionKey}] is undefined`);
    }
    return ret;
}
